import { useEffect, type RefObject } from 'react';

/**
 * FOCUS THE HEADING OF A VIEW THAT HAS JUST REPLACED ANOTHER — once, on mount.
 *
 * This package's convention: when a view or a step REPLACES what was on screen (a
 * recorded vote replacing the paper, the blank-paper warning replacing it, a setup step
 * or a point-of-no-return result replacing the hub, a confirm replacing the button that
 * asked for it), keyboard focus moves to that view's heading, and the heading carries
 * `tabIndex={-1}` so it is programmatically focusable without joining the tab order.
 * The focused heading IS the announcement — a live region mounted with its first message
 * goes unspoken in several screen readers, and without the move the keyboard drops to
 * `<body>` because the control it was on has unmounted. This hook exists to stop that
 * convention drifting: it was hand-rolled at every site as a `useRef`, a mount-only
 * `useEffect` and the `tabIndex`, and `setup/OpenStep.tsx` — the binding act — shipped
 * without the effect until a hand repair (P3a-1). One named call is harder to leave out
 * than a pair of lines copied from a neighbour.
 *
 * Exactly the hand-rolled pair's semantics, and no more: `ref.current?.focus()` in an
 * effect with an EMPTY dependency array. So it never re-focuses on a re-render (focus the
 * reader has moved stays where they put it), a ref still `null` at mount is a no-op, and
 * an element that appears LATER is not chased. A focus move on a TRANSITION inside a view
 * that is already mounted — `SetupPage`'s hub heading on return from a sub-view — is a
 * different act with its own condition, and is not this hook's.
 */
export function useFocusOnMount(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    ref.current?.focus();
    // Mount-only by design (see above). `ref` is a `useRef` object, stable for the
    // component's life, so leaving it out of the array changes nothing it could hold.
  }, []);
}
